import { Stack, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'

import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { mock } from '@/hooks/useGovernance'

import RootButton from '../../Buttons/RootButton'
import NominationModal from '../../Modals/Governance/NominateModal'
import StartElectionModal from '../../Modals/Governance/StartElectionModal'
import ModalSkeleton from '../../Modals/ModalSkeleton'
import NominationAction from '../GovernanceList/ListActions/NominationAction'
import NomineesList from '../GovernanceList/NomineesList'

interface NominationProps {}

const Nomination: React.FC<NominationProps> = () => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const { address } = useAccount()
  const [opened, { open, close }] = useDisclosure()
  const [openedElection, { open: openElection, close: closeElection }] = useDisclosure()
  const isDownMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  // Governance data
  const {
    // User
    hasNominated,
    isAdmin,
    // Election data
    electionData,
    nominationData,
  } = useGovernanceContext()

  // Modals
  const NominateModal = useMemo(
    () => (
      <ModalSkeleton
        opened={opened}
        onClose={close}
        size={isDownMd ? '' : '500px'}
        title={t('components.governance.nomination.modalTitle', 'Nominate Candidate')}
      >
        <NominationModal close={close} />
      </ModalSkeleton>
    ),
    [close, opened, t]
  )

  const ElectionModal = useMemo(
    () => (
      <ModalSkeleton
        opened={openedElection}
        onClose={closeElection}
        size={isDownMd ? '' : '500px'}
        title={t('components.governance.startElection.modalTitle', 'Start Election')}
      >
        <StartElectionModal close={closeElection} />
      </ModalSkeleton>
    ),
    [closeElection, openedElection, t]
  )

  // Check if an election can be started
  // If there is no election queued or the current election has ended
  const canStartElection = useMemo(
    () => electionData.idx === 0 || electionData.endTime < new Date().getTime() / 1000,
    [electionData]
  )

  // Has nomination period started
  const nominationStarted = useMemo(
    () => electionData.idx > 0 && electionData.nominationTime < new Date().getTime() / 1000,
    [electionData]
  )

  // Is nomination period active
  const nominationActive = useMemo(() => {
    const now = new Date().getTime() / 1000
    return electionData.nominationTime < now && electionData.campaignTime > now
  }, [electionData])

  return (
    <Stack>
      {isAdmin && canStartElection && (
        <RootButton style1={false} onClick={openElection}>
          {t('components.governance.startElection.button', 'Start Election')}
        </RootButton>
      )}
      {ElectionModal}
      {!nominationStarted && !mock ? (
        <Title order={4}>
          {t('components.governance.nomination.notStarted', 'Election not active yet')}
        </Title>
      ) : (
        <>
          <NomineesList
            candidates={nominationData}
            title={t('components.governance.nomination.title', 'Current Nominations')}
            button={
              !hasNominated &&
              address && (
                <RootButton disabled={!nominationActive} style1={false} onClick={open}>
                  {t('buttons.nominate', 'Nominate {{candidate}}', {
                    candidate: isMobile ? '' : 'Candidate',
                  })}
                </RootButton>
              )
            }
            action={NominationAction}
          />
          {NominateModal}
        </>
      )}
    </Stack>
  )
}

export default Nomination
